import { Spin } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppStore } from '../../store/store';

/**
 * Landing page after successful login when opened from the mobile app WebView.
 *
 * Flow:
 * 1. Mobile app opens a WebView at /login?mobileApp=1&email=user@example.com
 * 2. User authenticates (OTP or OAuth)
 * 3. After auth, user is redirected here
 * 4. This page appends token and refreshToken in the URL fragment (#)
 *    so the WebView can intercept them without exposing tokens in server logs or Referer headers
 * 5. The WebView intercepts the URL change and reads the tokens from the fragment
 */
export const MobileAuthSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const token = useAppStore((s) => s.token);
  const refreshToken = useAppStore((s) => s.customTokens?.rToken);
  const hasRedirected = useRef(false);

  // Check if tokens are already in the fragment (redirect already happened)
  const tokensInFragment = window.location.hash.includes('token=');

  useEffect(() => {
    // Tokens already in fragment — nothing to do, WebView will intercept
    if (tokensInFragment) return;

    // If no tokens are available, redirect back to login
    if (!token && !refreshToken) {
      navigate('/login');
      return;
    }

    // Prevent double execution (React Strict Mode)
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    // Put tokens in the URL fragment (#) so the WebView can intercept them.
    // Fragments are never sent to the server, avoiding token leakage in:
    // - Server/proxy access logs
    // - Referer headers to external resources
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    if (refreshToken) params.set('refreshToken', refreshToken);

    window.location.replace(`${window.location.pathname}#${params.toString()}`);
  }, [token, refreshToken, navigate, tokensInFragment]);

  // Clean up sessionStorage
  useEffect(() => {
    sessionStorage.removeItem('mobileApp');
  }, []);

  return (
    <div className="flex h-screen w-full items-center justify-center">
      <div className="text-center">
        <Spin size="large" />
        <p className="text-gray-dark mt-4">
          {t('auth.mobileRedirecting', { defaultValue: 'Redirecting to app...' })}
        </p>
      </div>
    </div>
  );
};

export default MobileAuthSuccess;
